import { bootstrapApplication } from '@angular/platform-browser';
import { ConfigurationService } from '@processpuzzle/util';
import { currentOrgKey, firstPathSegment, KeycloakAuthConfig } from '@processpuzzle/auth/domain';
import { environment as buildTimeEnv } from './environments/environment';
import { EnvironmentVariables } from './environments/environment-variables';
import { AppComponent } from './app/app.component';
import { createAppConfig } from './app/app.config';
import { RESERVED_PATH_SEGMENTS } from './app/app.routes';
import { withOrganizationRoots } from './app/organization-service-roots';
import { RuntimeConfiguration } from './app/runtime-configuration';

/**
 * The container's rendered environment, or the build-time one when the file is not there.
 *
 * `assets/runtime-env.json` is written by the Docker entrypoint from `envsubst`, which is what lets
 * one image serve every stage. `cache: 'no-store'` because a cached copy would pin the first stage
 * the browser ever saw.
 */
async function loadEnvironment(): Promise<EnvironmentVariables> {
  try {
    const response = await fetch('assets/runtime-env.json', { cache: 'no-store' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return (await response.json()) as EnvironmentVariables;
  } catch (error) {
    console.warn('runtime-env.json not available, using build-time environment', error);
    return buildTimeEnv as EnvironmentVariables;
  }
}

/**
 * The customer organization the URL names, or `undefined` when the first segment is one of this
 * application's own.
 *
 * `currentOrgKey()` answers with the first path segment whenever it *could* be an organization key,
 * and `looksLikeOrgKey('auth')` is true — so without this filter the Keycloak callback URL would be
 * read as a tenant named `auth`. Core deliberately does not filter reserved names inside the
 * resolver: that list lives on the backend and this code runs before the first HTTP call, so the
 * filtering belongs to the application that owns the segments.
 */
function organizationFromUrl(): string | undefined {
  const segment = firstPathSegment(typeof window === 'undefined' ? '/' : window.location.pathname);
  return segment !== undefined && RESERVED_PATH_SEGMENTS.includes(segment) ? undefined : currentOrgKey();
}

/**
 * Points authentication at the Keycloak origin this deployment actually runs, when the environment
 * names one.
 *
 * `run-time-conf/config.*.json` is baked into the image, and one image serves CI, local compose and
 * production. Those three share `PIPELINE_STAGE=prod` and therefore one configuration file, but they
 * do not share a Keycloak hostname, so the origin has to come from the environment; the file value
 * remains the fallback for every stage that needs no override.
 *
 * **The value must be Keycloak's own origin, never a path prefix of this one.** Keycloak scopes
 * `KEYCLOAK_SESSION` and `KEYCLOAK_IDENTITY` to `Path=/realms/<realm>/`, so an `/auth` proxy on this
 * origin puts every Keycloak page on a path those cookies do not match. The browser then withholds
 * them and two things fail silently: the login-status iframe reads no `KEYCLOAK_SESSION`, answers
 * `"changed"`, and keycloak-js clears the tokens it obtained seconds earlier — the next API call
 * refreshes with an undefined token, Keycloak logs `REFRESH_TOKEN_ERROR … "Invalid refresh token"`
 * and the request comes back 401 — while `prompt=none` returns `login_required`, so no session
 * survives a reload either. Both were observed on the local stack; neither is visible in a
 * healthcheck.
 *
 * Only `keycloak` configurations are touched.
 */
function withKeycloakUrl(env: EnvironmentVariables, runtimeConfiguration: RuntimeConfiguration): RuntimeConfiguration {
  const keycloakUrl = env.KEYCLOAK_URL?.trim();
  const authenticationConfiguration = runtimeConfiguration.AUTHENTICATION_CONFIGURATION;
  if (!keycloakUrl || authenticationConfiguration.AUTHENTICATION_PROVIDER !== 'keycloak') return runtimeConfiguration;

  const authServiceConfig = authenticationConfiguration.AUTH_SERVICE_CONFIG as KeycloakAuthConfig | undefined;
  return {
    ...runtimeConfiguration,
    AUTHENTICATION_CONFIGURATION: {
      ...authenticationConfiguration,
      AUTHENTICATION_SERVICE_ROOT: keycloakUrl,
      ...(authServiceConfig ? { AUTH_SERVICE_CONFIG: { ...authServiceConfig, authServerUrl: keycloakUrl } } : {}),
    },
  };
}

/**
 * Asks for the organization the URL names, `organization:<orgKey>`, so the token's `organization`
 * claim names exactly that one.
 *
 * The client's default `organization` scope maps one organization at most, and for an account in
 * several — a customer with a second application — none unless they picked one at login, which
 * nothing asks them to. Requesting all of them (`organization:*`) would not do either: the backend
 * reads a tenant's workflow roles from its organization's groups only when the token names exactly
 * one organization, so a second membership would silently cost the user their roles. One
 * organization per token is also what the per-organization bootstrap already assumes: switching is a
 * full page load, and that load logs in, by single sign-on, for the other organization.
 *
 * An account that is not a member of `orgKey` gets no claim at all and the backend answers 403, as
 * before.
 */
function withOrganizationScope(runtimeConfiguration: RuntimeConfiguration, orgKey?: string): RuntimeConfiguration {
  const authenticationConfiguration = runtimeConfiguration.AUTHENTICATION_CONFIGURATION;
  const authServiceConfig = authenticationConfiguration.AUTH_SERVICE_CONFIG as KeycloakAuthConfig | undefined;
  if (!orgKey || authenticationConfiguration.AUTHENTICATION_PROVIDER !== 'keycloak' || !authServiceConfig) return runtimeConfiguration;
  return {
    ...runtimeConfiguration,
    AUTHENTICATION_CONFIGURATION: {
      ...authenticationConfiguration,
      AUTH_SERVICE_CONFIG: { ...authServiceConfig, scope: `organization:${orgKey}` },
    },
  };
}

/**
 * Whether Keycloak just refused this organization: the login came back with `invalid_scope` for
 * `organization:<orgKey>`, which is what it answers when no organization has that alias.
 *
 * Checked before bootstrap, and the error is removed from the URL. Left there, keycloak-js would
 * process the same callback again during initialisation, fail it, and the login guard would turn the
 * failure into an unhandled exception on every reload. The application then starts as though the
 * URL named no organization, and the no-organization page says that this one does not exist — rather
 * than sending the visitor back to a login Keycloak will refuse again.
 *
 * An organization that exists but that the account is not a member of is not refused: the token
 * simply names no organization, and the backend answers 403, as it always has.
 */
function refusedOrganization(orgKey: string | undefined): boolean {
  if (!orgKey || typeof window === 'undefined') return false;
  const callback = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  if (callback.get('error') !== 'invalid_scope' || !(callback.get('error_description') ?? '').includes(`organization:${orgKey}`)) return false;
  window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
  return true;
}

/**
 * **No realm substitution here, unlike `processpuzzle-biz-frontend` — and that is the whole point of
 * this application.**
 *
 * Biz serves tenants that each had their own Keycloak realm, so it had to rewrite
 * `AUTH_SERVICE_CONFIG.realm` from the URL before bootstrapping. Customers do not work that way:
 * they all live in the single `processpuzzle-custom` realm and are told apart by a Keycloak
 * **Organization**, whose membership the access token carries as an `organization` claim
 * (`docs/customer-application-seed.md` §2, `docs/processpuzzle-architecture-summary.md` §5). The
 * realm is therefore a constant of the deployment and belongs in `run-time-conf`, where it is.
 *
 * The `orgKey` still comes out of the URL, because it decides which routes exist and which
 * organization's data the backend is asked for — it just no longer decides *who signs the token*.
 * That is the security-relevant half of the difference: a customer cannot reach another
 * organization's data by editing the URL, because the token they already hold names their
 * organization and `CustomerOrganizationAccessPolicy` on the backend compares the two.
 */
async function bootstrap() {
  const env = await loadEnvironment();
  const configurationService = new ConfigurationService<EnvironmentVariables, RuntimeConfiguration>();
  const runtimeConfiguration = withKeycloakUrl(env, (await configurationService.init(env)) as RuntimeConfiguration);

  // Read once and handed to the application as a value. `ORG_ADMIN_ORG_KEY` is injected when a
  // root-provided service is constructed, which is long before any route activates, so it cannot be
  // a route parameter — and the routes themselves are built around this key, so a URL naming a
  // different organization does not match at all and the browser reloads.
  const urlOrgKey = organizationFromUrl();
  const unknownOrgKey = refusedOrganization(urlOrgKey) ? urlOrgKey : undefined;
  const orgKey = unknownOrgKey ? undefined : urlOrgKey;

  // The same key scopes the service roots: `run-time-conf` can only name the host, because one image
  // serves every organization. See `withOrganizationRoots`.
  await bootstrapApplication(
    AppComponent,
    createAppConfig(withOrganizationScope(withOrganizationRoots(runtimeConfiguration, orgKey), orgKey), orgKey, unknownOrgKey),
  );
}

bootstrap().catch((error) => console.error(error));
